import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { Card, Stack, TableContainer, Table, TableBody, TableRow, TableCell, Box, Paper, Typography } from '@mui/material';
import { Facebook, Share, LinkedIn, Groups, X, QrCode2, AltRoute } from '@mui/icons-material';
import IconTooltip from '../IconTooltip';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import renderPercentage from '../renderPercentage';
import useApi from '../../hooks/useApi';
import ReportsCardHeader from '../cards/ReportsCardHeader';
import ReportsCardContent from '../cards/ReportsCardContent';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';
import { grey } from '@mui/material/colors';
import ChannelsChart from '../charts/ChannelsChart';
import { channelColours } from '../../theme/constants';
import TypedValue from '../../TypedValue';

const ActionsTable = ({ type, totals, campaignUniqueOpens, channelName, showShares = true }) => {
    return (
        <Paper variant="outlined">
            <TableContainer>
                <Table size="small">
                    <TableBody>
                        {showShares && (
                            <TableRow>
                                <TableCell component="th" width="100%">{(type === 'Twitter' || type === 'X') ? 'Posts' : 'Shares'}</TableCell>
                                <TableCell align="right">
                                    <TypedValue value={totals.shares} accuracy={0} type="number" />
                                </TableCell>
                                <TableCell align="right">{renderPercentage(totals.shares, campaignUniqueOpens)}</TableCell>
                                <TableCell>
                                    <IconTooltip title={`Shares %: ${channelName} Shares divided by Unique Email Opens`} />
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell component="th" width="100%">Opens</TableCell>
                            <TableCell align="right">
                                <TypedValue value={totals.opens} accuracy={0} type="number" />
                            </TableCell>
                            <TableCell align="right">{showShares ? renderPercentage(totals.opens, totals.shares) : '-'}</TableCell>
                            <TableCell>
                                {showShares && (
                                    <IconTooltip title={`Opens %: ${channelName} Opens divided by ${channelName} Shares`} />
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" width="100%" sx={{ border: 0 }}>Clicks</TableCell>
                            <TableCell align="right" sx={{ border: 0 }}>
                                <TypedValue value={totals.clicks} accuracy={0} type="number" />
                            </TableCell>
                            <TableCell align="right" sx={{ border: 0 }}>{renderPercentage(totals.clicks, totals.opens)}</TableCell>
                            <TableCell sx={{ border: 0 }}>
                                <IconTooltip title={`Clicks %: ${channelName} Clicks divided by ${channelName} Opens`} />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const Channel = ({ type, actions, campaignUniqueOpens, icon, title = null, showShares }) => {
    const theme = useTheme();

    const getTotal = type => {
        const actionType = actions.find(e => e.label.toLowerCase().includes(type));

        return actionType ? actionType.total : 0;
    };

    const totals = {
        shares: getTotal('post'),
        opens: getTotal('open'),
        clicks: getTotal('click')
    };

    const totalActivity = Object.values(totals).reduce((a, b) => a + b, 0);

    return (
        <Card>
            <ReportsCardHeader
                title={title ?? type}
                icon={icon}
            />
            <ReportsCardContent>
                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ backgroundColor: lighten(theme.palette.primary.main, 0.9) }}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell variant="head">
                                            Total
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                <TypedValue value={totalActivity} accuracy={0} type="number" />
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <ActionsTable
                        actions={actions}
                        type={type}
                        totals={totals}
                        campaignUniqueOpens={campaignUniqueOpens}
                        showShares={showShares}
                        channelName={title ?? type}
                    />
                </Stack>
            </ReportsCardContent>
        </Card>
    );
};

const Channels = ({ id, uniqueOpens }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const { handleGet } = useApi();

    const handleGetData = async () => {
        const url = `reports/${id}/channels`;
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        setData(await response.json());
    };

    useEffect(() => {
        handleGetData();
    }, []);

    useEffect(() => {
        data && setIsLoading(false);
    }, [data]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <Box sx={{ margin: 2 }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Card>
                        <ReportsCardHeader
                            title="Channel Performance"
                            icon={<AltRoute />}
                        />
                        <ReportsCardContent paddingBottom={2}>
                            <ChannelsChart data={data} />
                        </ReportsCardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="QR"
                        actions={data.filter(e => e.type.toLowerCase() === 'qr')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<QrCode2 sx={{ color: grey[600] }} />}
                        title="QR Codes"
                        showShares={false}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="Teams"
                        actions={data.filter(e => e.type.toLowerCase() === 'teams')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<Groups sx={{ color: channelColours.teams }} />}
                        showShares={false}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="Share"
                        actions={data.filter(e => e.type.toLowerCase() === 'share')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<Share sx={{ color: grey[600] }} />}
                        title="Share Link"
                        showShares={false}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="Facebook"
                        actions={data.filter(e => e.type.toLowerCase() === 'facebook')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<Facebook sx={{ color: channelColours.facebook }} />}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="Twitter"
                        actions={data.filter(e => e.type.toLowerCase() === 'twitter' || e.type.toLowerCase() === 'x')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<X sx={{ color: channelColours.x }} />}
                        title="X"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <Channel
                        type="LinkedIn"
                        actions={data.filter(e => e.type.toLowerCase() === 'linkedin')}
                        campaignUniqueOpens={uniqueOpens}
                        icon={<LinkedIn sx={{ color: channelColours.linkedin }} />}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Channels;